import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus, Send, Tag as TagIcon, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtDate, statusColor } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ClientFormDialog from "@/components/ClientFormDialog";

const FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "ativos", label: "Ativos" },
  { key: "inativos", label: "Inativos" },
  { key: "novos", label: "Novos" },
  { key: "com_whatsapp", label: "Com WhatsApp" },
  { key: "sem_whatsapp", label: "Sem WhatsApp" },
];

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("todos");
  const [tags, setTags] = useState([]);
  const [tagFilter, setTagFilter] = useState("");
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDelete, setBulkDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = { filter };
    if (search) params.search = search;
    if (tagFilter) params.tag = tagFilter;
    api.get("/clients", { params }).then((r) => { setClients(r.data); setLoading(false); });
  }, [filter, search, tagFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/tags").then((r) => setTags(r.data)); }, []);

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = clients.length > 0 && selected.length === clients.length;

  const openNew = () => { setEditClient(null); setDialogOpen(true); };
  const openEdit = (c) => { setEditClient(c); setDialogOpen(true); };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/clients/${deleteTarget.id}`);
      toast.success("Cliente excluído.");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const confirmBulk = async () => {
    if (!bulkDelete) return;
    try {
      const payload = bulkDelete.type === "all" ? { all: true } : { ids: selected };
      const { data } = await api.post("/clients/bulk-delete", payload);
      toast.success(`${data.deleted} cliente(s) excluído(s).`);
      setBulkDelete(null);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const startCampaign = () => {
    if (selected.length === 0) return toast.error("Selecione ao menos um cliente.");
    navigate("/disparos", { state: { clientIds: selected } });
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="clients-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Clientes</h1>
          <p className="text-sm text-slate-500 mt-1">{clients.length} clientes encontrados.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selected.length > 0 && (
            <>
              <Button onClick={startCampaign} variant="outline" className="border-emerald-200 text-emerald-700" data-testid="clients-dispatch-selected">
                <Send className="h-4 w-4 mr-2" /> Disparar ({selected.length})
              </Button>
              <Button onClick={() => setBulkDelete({ type: "selected", count: selected.length })} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="clients-delete-selected">
                <Trash2 className="h-4 w-4 mr-2" /> Excluir ({selected.length})
              </Button>
            </>
          )}
          {clients.length > 0 && (
            <Button onClick={() => setBulkDelete({ type: "all" })} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="clients-delete-all">
              <Trash2 className="h-4 w-4 mr-2" /> Excluir todos
            </Button>
          )}
          <Button onClick={openNew} className="bg-emerald-500 hover:bg-emerald-600" data-testid="clients-add-button">
            <UserPlus className="h-4 w-4 mr-2" /> Novo Cliente
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Pesquisar por nome, WhatsApp ou e-mail..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="client-search-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${filter === f.key ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>
              {f.label}
            </button>
          ))}
          <div className="w-px bg-slate-200 mx-1" />
          <button onClick={() => setTagFilter("")} className={`text-xs font-medium px-3 py-1.5 rounded-full border flex items-center gap-1 ${!tagFilter ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}>
            <TagIcon className="h-3 w-3" /> Todas tags
          </button>
          {tags.map((t) => (
            <button key={t.id} onClick={() => setTagFilter(t.name)} data-testid={`tag-filter-${t.name}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border ${tagFilter === t.name ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>
              {t.name} <span className="opacity-60">{t.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left w-10">
                  <Checkbox checked={allSelected} onCheckedChange={(v) => setSelected(v ? clients.map((c) => c.id) : [])} />
                </th>
                <th className="px-4 py-3 text-left">Nome</th>
                <th className="px-4 py-3 text-left">WhatsApp</th>
                <th className="px-4 py-3 text-left hidden md:table-cell">E-mail</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">Cadastro</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">Último contato</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left hidden xl:table-cell">Tags</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={9} className="text-center py-12 text-slate-400">Carregando...</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-12 text-slate-400">Nenhum cliente encontrado.</td></tr>
              ) : clients.map((c) => (
                <tr key={c.id} className="hover:bg-emerald-50/40 transition-colors" data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggleSel(c.id)} data-testid={`client-select-${c.id}`} />
                  </td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/clientes/${c.id}`)}>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold shrink-0">{c.name?.[0]}</div>
                      <span className="font-medium text-slate-800">{c.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.whatsapp || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{c.email || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 hidden lg:table-cell">{fmtDate(c.created_at)}</td>
                  <td className="px-4 py-3 text-slate-500 hidden lg:table-cell">{fmtDate(c.last_contact)}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${statusColor(c.status)}`}>{c.status}</span></td>
                  <td className="px-4 py-3 hidden xl:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(c.tags || []).slice(0, 2).map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t}</span>)}
                      {(c.tags || []).length > 2 && <span className="text-[10px] text-slate-400">+{c.tags.length - 2}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={(e) => { e.stopPropagation(); openEdit(c); }} title="Editar" data-testid={`client-edit-${c.id}`}
                        className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 transition-colors">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }} title="Excluir" data-testid={`client-delete-${c.id}`}
                        className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ClientFormDialog open={dialogOpen} onOpenChange={setDialogOpen} client={editClient} onSaved={load} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-client-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cliente</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <span className="font-semibold text-slate-700">{deleteTarget?.name}</span>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-client">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!bulkDelete} onOpenChange={(o) => !o && setBulkDelete(null)}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkDelete?.type === "all" ? "Excluir TODOS os clientes" : "Excluir clientes selecionados"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkDelete?.type === "all"
                ? "Isso vai excluir permanentemente TODOS os clientes, junto com o histórico de eventos e os follow-ups deles. Esta ação não pode ser desfeita."
                : `Tem certeza que deseja excluir ${bulkDelete?.count} cliente(s) selecionado(s)? Esta ação não pode ser desfeita.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulk} className="bg-red-500 hover:bg-red-600" data-testid="confirm-bulk-delete">
              {bulkDelete?.type === "all" ? "Excluir todos" : "Excluir selecionados"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
